import { useState } from "react";
import { Link } from "react-router-dom";
import { House, EnvelopeSimple, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/password-reset/request", { email });
      setSent(true);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sand-100">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1631630259742-c0f0b17c6c10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxjb3p5JTIwaG9tZSUyMGludGVyaW9yJTIwd2FybXxlbnwwfHx8fDE3ODE3MjQxNzB8MA&ixlib=rb-4.1.0&q=85"
          alt="Cozy home interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-stoneink-900/55 via-stoneink-900/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-heading text-4xl xl:text-5xl font-semibold leading-tight">It happens<br/>to the best of us.</div>
          <p className="mt-4 text-white/85 max-w-md">A new password is just an email away.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-12 w-12 rounded-2xl bg-terracotta-500 text-white grid place-items-center shadow-ambient">
              <House weight="duotone" size={26} />
            </div>
            <div>
              <div className="font-heading text-2xl font-semibold text-stoneink-900">Nestled</div>
              <div className="text-xs text-stoneink-500">welcome home</div>
            </div>
          </div>

          {sent ? (
            <div data-testid="reset-sent">
              <div className="h-14 w-14 rounded-2xl bg-sage-400/20 text-sage-600 grid place-items-center">
                <CheckCircle weight="duotone" size={28} />
              </div>
              <h1 className="mt-5 font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Check your inbox</h1>
              <p className="mt-3 text-stoneink-700">
                If <span className="font-medium text-stoneink-900">{email}</span> belongs to a Nestled account, we&apos;ve sent a reset link. It expires in 30 minutes.
              </p>
              <p className="mt-3 text-stoneink-500 text-sm">
                Don&apos;t see it? Check spam, or{" "}
                <button onClick={() => setSent(false)} className="text-terracotta-600 hover:text-terracotta-700 underline-offset-4 hover:underline" data-testid="resend-link">
                  try another email
                </button>.
              </p>
              <Link to="/login" data-testid="back-to-login" className="mt-8 inline-flex items-center gap-2 text-stoneink-700 hover:text-terracotta-600 text-sm">
                <ArrowLeft size={16} /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Reset your password</h1>
              <p className="mt-3 text-stoneink-700">Enter your email and we&apos;ll send you a link to choose a new one.</p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Email</label>
                  <div className="mt-2 relative">
                    <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneink-500" />
                    <input
                      data-testid="forgot-email-input"
                      type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-12 pl-10 pr-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                      placeholder="you@home.app"
                      autoFocus
                    />
                  </div>
                </div>
                {error && <div className="text-sm text-destructive" data-testid="forgot-error">{error}</div>}
                <button
                  data-testid="forgot-submit-btn"
                  type="submit" disabled={busy}
                  className="w-full h-12 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium transition-all active:scale-[0.99] disabled:opacity-60"
                >
                  {busy ? "Sending…" : "Send reset link"}
                </button>
              </form>

              <Link to="/login" data-testid="back-to-login-link" className="mt-8 inline-flex items-center gap-2 text-stoneink-700 hover:text-terracotta-600 text-sm">
                <ArrowLeft size={16} /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
